import { Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { GenericCrudEditComponent } from 'src/app/libs/components/generic-crud/generic-crud-edit.component';
import {
  BatchCodeDefinitions,
  BatchSelectionCriteria,
  BatchSplittingMode,
  BatchTraceability,
  BatchType,
  EBatchCodeDefinition,
  EBatchTraceability,
  EProviderBatchCodeDefinition,
  ProviderBatchCodeDefinitions,
} from 'src/app/models';

@Component({
  selector: 'app-batch-types-edit',
  templateUrl: './batch-types-edit.component.html',
  styleUrl: './batch-types-edit.component.sass',
  standalone: false,
})
export class BatchTypesEditComponent extends GenericCrudEditComponent<BatchType> {
  override resource = this.api.resources.product.batchTypes;
  productBatchStates = this.api.resources.product.productBatchStates;

  batchCodeDefinitions: { code: number; name: string }[] = [];
  providerBatchCodeDefinitions: { code: number; name: string }[] = [];
  batchSelectionCriteriaItems: { code: number; name: string }[] = [];
  batchTraceabilityItems: { code: number; name: string }[] = [];
  batchSplittingModeItems: { code: number; name: string }[] = [];

  protected override async preFetch(): Promise<void> {
    const keys = [
      ...BatchCodeDefinitions.map((x) => `enumeration.BatchCodeDefinition.${x.name}`),
      ...ProviderBatchCodeDefinitions.map((x) => `enumeration.ProviderBatchCodeDefinition.${x.name}`),
      ...BatchSelectionCriteria.map((x) => `enumeration.BatchSelectionCriteria.${x.name}`),
      ...BatchTraceability.map((x) => `enumeration.BatchTraceability.${x.name}`),
      ...BatchSplittingMode.map((x) => `enumeration.BatchSplittingMode.${x.name}`),
    ];
    await firstValueFrom(this.translationService.get(keys));
    this.batchCodeDefinitions = BatchCodeDefinitions.map((x) => ({
      code: x.id,
      name: this.translationService.instant(`enumeration.BatchCodeDefinition.${x.name}`),
    }));

    this.providerBatchCodeDefinitions = ProviderBatchCodeDefinitions.map((x) => ({
      code: x.id,
      name: this.translationService.instant(`enumeration.ProviderBatchCodeDefinition.${x.name}`),
    }));

    this.batchSelectionCriteriaItems = BatchSelectionCriteria.map((x) => ({
      code: x.id,
      name: this.translationService.instant(`enumeration.BatchSelectionCriteria.${x.name}`),
    }));

    this.batchTraceabilityItems = BatchTraceability.map((x) => ({
      code: x.id,
      name: this.translationService.instant(`enumeration.BatchTraceability.${x.name}`),
    }));

    this.batchSplittingModeItems = BatchSplittingMode.map((x) => ({
      code: x.id,
      name: this.translationService.instant(`enumeration.BatchSplittingMode.${x.name}`),
    }));
  }

  displayEntity = () => this.entity!.name;

  initFormGroup() {
    const form = this.fb.group({
      id: [this.entity!.id],
      name: [this.entity!.name, Validators.required],
      companyId: [this.entity!.companyId],
      batchTraceability: [this.entity!.batchTraceability, Validators.required],
      batchCodeDefinition: [this.entity!.batchCodeDefinition],
      providerBatchCodeDefinition: [this.entity!.providerBatchCodeDefinition],
      batchSelectionCriteria: [this.entity!.batchSelectionCriteria],
      batchSplittingMode: [this.entity!.batchSplittingMode],
      defaultBatchStateId: [this.entity!.defaultBatchStateId, Validators.required],
      batchCodeRangeMin: [this.entity!.batchCodeRangeMin ?? null],
      batchCodeRangeMax: [this.entity!.batchCodeRangeMax ?? null],
      providerBatchCodeRangeMin: [this.entity!.providerBatchCodeRangeMin ?? null],
      providerBatchCodeRangeMax: [this.entity!.providerBatchCodeRangeMax ?? null],
      batchCodeUseEan13: [this.entity!.batchCodeUseEan13 ?? false],
      providerBatchCodeUseEan13: [this.entity!.providerBatchCodeUseEan13 ?? false],
    });

    this.updateTraceabilityDependentValidators(form.value.batchTraceability);
    this.updateCodeDefinitionDependentFields(form.value.batchCodeDefinition);
    this.updateProviderCodeDefinitionDependentFields(form.value.providerBatchCodeDefinition);
    return form;
  }

  isBatch:boolean = false;
  isSerialNumber:boolean = false;
  isSequentialCode: boolean = false;
  isSequentialProviderCode: boolean = false;

  updateTraceabilityDependentValidators(value?: EBatchTraceability | number | null) {
    const batchTraceabilitySelected = Number(value);

    this.isBatch = batchTraceabilitySelected === EBatchTraceability.Batch;
    this.isSerialNumber = batchTraceabilitySelected === EBatchTraceability.SerialNumber;

    if (batchTraceabilitySelected === EBatchTraceability.Batch) {
      this.addValidation('batchCodeDefinition');
      this.addValidation('providerBatchCodeDefinition');
      this.addValidation('batchSelectionCriteria');
      this.addValidation('batchSplittingMode');
    } else if (batchTraceabilitySelected === EBatchTraceability.WithoutBatch) {
      this.removeValidation('batchCodeDefinition');
      this.removeValidation('providerBatchCodeDefinition');
      this.removeValidation('batchSelectionCriteria');
      this.removeValidation('batchSplittingMode');
      this.removeValidation('batchCodeRangeMin');
      this.removeValidation('batchCodeRangeMax');
      this.removeValidation('providerBatchCodeRangeMin');
      this.removeValidation('providerBatchCodeRangeMax');
    } else if (batchTraceabilitySelected === EBatchTraceability.SerialNumber) {
      this.addValidation('batchCodeDefinition');
      this.addValidation('providerBatchCodeDefinition');
      this.addValidation('batchSelectionCriteria');
      this.addValidation('batchSplittingMode');
    }
  }

  private addValidation(control: string) {
    const formControl = this.form?.get(control);
    if (!formControl) {
      return;
    }

    formControl.setValidators([Validators.required]);

    if (formControl.value === undefined || Number.isNaN(formControl.value)) {
      formControl.setValue(null);
    }

    formControl.updateValueAndValidity();
  }

  private removeValidation(control: string) {
    const formControl = this.form?.get(control);
    if (!formControl) {
      return;
    }

    formControl.clearValidators();
    formControl.setValue(null);
    formControl.updateValueAndValidity();
  }

  updateCodeDefinitionDependentFields(value?: EBatchCodeDefinition | number | null) {
    const batchCodeDefinitionSelected = Number(value);

    this.isSequentialCode =
      batchCodeDefinitionSelected === EBatchCodeDefinition.Sequential ||
      batchCodeDefinitionSelected === EBatchCodeDefinition.SequentialWithYearPrefix;

    if (this.isSequentialCode) {
      this.addValidation('batchCodeRangeMin');
      this.addValidation('batchCodeRangeMax');
    } else {
      this.removeValidation('batchCodeRangeMin');
      this.removeValidation('batchCodeRangeMax');
    }
  }

  updateProviderCodeDefinitionDependentFields(value?: EProviderBatchCodeDefinition | number | null) {
    const providerBatchCodeDefinitionSelected = Number(value);

    this.isSequentialProviderCode =
      providerBatchCodeDefinitionSelected === EProviderBatchCodeDefinition.Sequential ||
      providerBatchCodeDefinitionSelected === EProviderBatchCodeDefinition.SequentialWithYearPrefix;

    if (this.isSequentialProviderCode) {
      this.addValidation('providerBatchCodeRangeMin');
      this.addValidation('providerBatchCodeRangeMax');
    } else {
      this.removeValidation('providerBatchCodeRangeMin');
      this.removeValidation('providerBatchCodeRangeMax');
    }
  }
}
